import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartItem } from '../../types/order';

interface CartState {
  items: CartItem[];
  subtotal: number;
  total: number;
  taxes: number;
  isOpen: boolean;
}

const initialState: CartState = {
  items: [],
  subtotal: 0,
  total: 0,
  taxes: 0,
  isOpen: false,
};

const calculateTotals = (items: CartItem[]) => {
  const subtotal = items.reduce((sum, item) => sum + (item.rate * item.quantity), 0);
  const taxes = subtotal * 0.08; // 8% tax
  const total = subtotal + taxes;

  return { subtotal, taxes, total };
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addItem: (state, action: PayloadAction<Omit<CartItem, 'id' | 'addedAt' | 'amount'>>) => {
      const existingItem = state.items.find(item =>
        item.itemCode === action.payload.itemCode &&
        item.customizations?.join(',') === action.payload.customizations?.join(',')
      );

      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        existingItem.amount = existingItem.quantity * existingItem.rate;
      } else {
        const newItem: CartItem = {
          ...action.payload,
          id: `${action.payload.itemCode}_${Date.now()}`,
          addedAt: new Date().toISOString(),
          amount: action.payload.rate * action.payload.quantity,
        };
        state.items.push(newItem);
      }

      const totals = calculateTotals(state.items);
      state.subtotal = totals.subtotal;
      state.taxes = totals.taxes;
      state.total = totals.total;
    },
    removeItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter(item => item.id !== action.payload);

      const totals = calculateTotals(state.items);
      state.subtotal = totals.subtotal;
      state.taxes = totals.taxes;
      state.total = totals.total;
    },
    updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
      const item = state.items.find(item => item.id === action.payload.id);
      if (item) {
        if (action.payload.quantity <= 0) {
          state.items = state.items.filter(item => item.id !== action.payload.id);
        } else {
          item.quantity = action.payload.quantity;
          item.amount = item.rate * item.quantity;
        }
      }

      const totals = calculateTotals(state.items);
      state.subtotal = totals.subtotal;
      state.taxes = totals.taxes;
      state.total = totals.total;
    },
    clearCart: (state) => {
      state.items = [];
      state.subtotal = 0;
      state.taxes = 0;
      state.total = 0;
    },
    toggleCart: (state) => {
      state.isOpen = !state.isOpen;
    },
    setCartOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
    },
    addVoiceItem: (state, action: PayloadAction<{
      itemCode: string;
      itemName: string;
      rate: number;
      quantity: number;
      voiceCommand?: string;
      confidence?: number;
    }>) => {
      const existingItem = state.items.find(item => item.itemCode === action.payload.itemCode);

      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
        existingItem.amount = existingItem.quantity * existingItem.rate;
      } else {
        const newItem: CartItem = {
          id: `voice_${action.payload.itemCode}_${Date.now()}`,
          itemCode: action.payload.itemCode,
          itemName: action.payload.itemName,
          quantity: action.payload.quantity,
          rate: action.payload.rate,
          amount: action.payload.rate * action.payload.quantity,
          addedAt: new Date().toISOString(),
          specialInstructions: action.payload.voiceCommand ? `Voice: "${action.payload.voiceCommand}"` : undefined,
        };
        state.items.push(newItem);
      }

      const totals = calculateTotals(state.items);
      state.subtotal = totals.subtotal;
      state.taxes = totals.taxes;
      state.total = totals.total;
    },
  },
});

export const {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  toggleCart,
  setCartOpen,
  addVoiceItem,
} = cartSlice.actions;

export default cartSlice.reducer;